import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import type { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";
import type { BulkUploadApiResponseDto } from "../../../masters/data/dto/BulkUploadDto";
import {
  BulkVerifyForReviewError,
  type BulkVerifyForReviewFailure,
} from "../../../masters/data/api/bulkUploadApi";

const VALID_EXTENSIONS = [".xlsx", ".xls"];

function assertExcelFile(file: File): void {
  const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
  if (VALID_EXTENSIONS.indexOf(ext) === -1) {
    throw new Error("Only Excel files (.xlsx, .xls) are allowed");
  }
}

async function downloadTemplate(path: string, fallbackFilename: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { message?: string }).message ||
        `Failed to download template: ${response.statusText}`,
    );
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = fallbackFilename;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

async function postUpload(path: string, file: File): Promise<BulkUploadApiResponseDto> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  assertExcelFile(file);

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const data = (await response.json()) as BulkUploadApiResponseDto;
  if (!response.ok) {
    throw new Error(data.message || `Upload failed: ${response.statusText}`);
  }
  return data;
}

interface VerifyBulkReviewApiResponse {
  success: boolean;
  message: string;
  data?: {
    change_details?: ChangeDetails;
    meta?: unknown;
    invalid_rows?: BulkVerifyForReviewFailure[];
  };
}

async function postVerifyForReview(path: string, file: File): Promise<ChangeDetails> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  assertExcelFile(file);

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const body = (await response.json()) as VerifyBulkReviewApiResponse;
  if (!response.ok || !body.success) {
    const invalidRows = body.data?.invalid_rows ?? [];
    throw new BulkVerifyForReviewError(
      body.message || "Verification failed: some rows are not valid",
      invalidRows,
    );
  }
  const cd = body.data?.change_details;
  if (!cd) {
    throw new Error("Server did not return change_details");
  }
  return cd;
}

export const downloadGlsTemplate = async (): Promise<void> => {
  // Server picks Zoho vs Tally template from company accounting_backend.
  await downloadTemplate("/api/gl-maps/template/bulk", "gls_template.xlsx");
};

export const uploadGlsFile = async (file: File): Promise<BulkUploadApiResponseDto> => {
  return postUpload("/api/gl-maps/upload/bulk", file);
};

export const verifyGlsBulkForReview = async (file: File): Promise<ChangeDetails> => {
  return postVerifyForReview("/api/gl-maps/upload/bulk/verify-for-review", file);
};

export const applyGlBulkReview = async (reviewId: number) => {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await fetch(`${API_BASE_URL}/api/gl-maps/bulk-review/apply`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ review_id: reviewId }),
  });

  const body = await response.json();
  if (!response.ok || !body.success) {
    throw new Error(body.message || `Apply failed: ${response.statusText}`);
  }
  return body;
};
